import { Axios } from 'utils'
import {
  TCalculateMixer,
  TCalculateSuggestion,
  TLimitation,
  TAttribute,
  TBlend,
  TBins,
} from 'types/models'
import {
  ItemResponseType,
  ListPaginationParams,
  ListResponseType,
} from './types'

export const useBlendingService = () => {
  const createBlends = async (params: Partial<TBlend>) => {
    try {
      const response = await Axios.post<ItemResponseType<TBlend>>(
        '/blends',
        params
      )
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const updateBlends = async (params: Partial<TBlend>) => {
    try {
      const response = await Axios.put<ItemResponseType<TBlend>>(
        `/blends/${params.id}`,
        params
      )
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const getBlends = async (pagination?: ListPaginationParams) => {
    try {
      const response = await Axios.get<ListResponseType<TBlend>>('/blends', {
        params: pagination
          ? {
              offset: pagination.currentPage * pagination.itemsPerPage,
              limit: pagination.itemsPerPage,
            }
          : undefined,
      })
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const getSavedBlends = async (pagination?: ListPaginationParams) => {
    try {
      const response = await Axios.get<ListResponseType<TBlend>>(
        '/blends/accepted',
        {
          params: pagination
            ? {
                offset: pagination.currentPage * pagination.itemsPerPage,
                limit: pagination.itemsPerPage,
              }
            : undefined,
        }
      )
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const deleteBlend = async (id: number) => {
    try {
      const response = await Axios.delete<{ status: boolean; detail: string }>(
        `/blends/${id}`
      )
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const getLimitations = async () => {
    try {
      const response = await Axios.get<ListResponseType<TLimitation>>(
        '/blends/limitations'
      )
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const getBlendById = async (blendId?: string) => {
    try {
      const response = await Axios.get<ItemResponseType<TBlend>>(
        `/blends/${blendId}`
      )
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const calculateBlendSuggestions = async (
    limitations: Partial<TLimitation>[],
    productAttributes: TAttribute[]
  ) => {
    try {
      const response = await Axios.post<
        ItemResponseType<{
          detail?: string
          status?: string
          suggestions: TCalculateSuggestion[]
          mixers: TCalculateMixer[]
        }>
      >('/blends/calculate', {
        limitations,
        product_attributes: productAttributes,
      })
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const calculateBlendMixer = async (
    mixerBins: Partial<TBins>[],
    productAttributes: TAttribute[]
  ) => {
    try {
      const response = await Axios.post<
        ItemResponseType<{
          status?: string
          detail?: string
          mixer_cost: number
          mixer_attributes: TAttribute[]
        }>
      >('/blends/calculate_blend_mixer', {
        mixer_bins: mixerBins,
        product_attributes: productAttributes,
      })
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const checkExistingBlendName = async (name: string) => {
    try {
      const response = await Axios.post<string>(`/blends/check-name`, { name })
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const sendBlendPdf = async (params: {
    blob: Blob
    filename: string
    blendId: number
  }) => {
    try {
      const formData = new FormData()
      formData.append('file', params.blob, params.filename)
      formData.append('blend_id', String(params.blendId))

      const response = await Axios.post<{ status: string }>(
        '/blends/send-pdf',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  return {
    createBlends,
    updateBlends,
    getBlends,
    getSavedBlends,
    deleteBlend,
    getLimitations,
    getBlendById,
    calculateBlendSuggestions,
    calculateBlendMixer,
    checkExistingBlendName,
    sendBlendPdf,
  }
}
